import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { ERROR_TYPE, toastFailure } from "@/utils";

import {
	useCreateLiPostMutation,
	useUpdateLiPostMutation,
} from "@/pages/Private/redux/liPost/liPost.api";

import { LiPostType } from "@/enum/liprofile.enum";

import { resolveSourceMode } from "@/pages/Private/pages/List/pages/ListCreate/utils/sourceMode";

import {
	CreateLiPost,
	CreateLiPostSchema,
	LI_POST_QUERY_TOO_LONG_MESSAGE,
	LiPost,
} from "../schema/liPost";
import { List } from "../../List/schema/list";

import { initialState } from "../constants/createLiPost.constants";

import { TopicQueryBuilder } from "./TopicQueryBuilder/TopicQueryBuilder";
import { LiPostTypeSelector } from "./LiPostTypeSelector";
import { ProfileSourceSection } from "./ProfileSourceSection";
import { PostUrlSection } from "./PostUrlSection";
import { TimeFrameField } from "./TimeFrameField";
import { ProfileQueryField } from "./ProfileQueryField";
import { TopicPostFiltersSection } from "./TopicPostFiltersSection";
import { LeadListSourceSection } from "./LeadListSourceSection";
import { LiPostFormActions } from "./LiPostFormActions";
import { useLinkedinSuggestions } from "./useLinkedinSuggestions";
import { usePostUrlValidation } from "./usePostUrlValidation";
import {
	hasAdditionalColumns,
	mapLiPostToFormState,
	normalizeLiPostPayload,
} from "./liPostPayload";

interface CreateLiPostFormProps {
	liPost?: LiPost | null;
	list: List;
	handleSelect?: (company: LiPost | null) => void;
	handleDuplicate?: (company: LiPost | null) => void;
}

export const CreateLiPostForm: FunctionComponent<CreateLiPostFormProps> = ({
	liPost,
	list,
	handleSelect,
	handleDuplicate,
}) => {
	const { t } = useTranslation();

	const [createLiPost, { error, isLoading }] = useCreateLiPostMutation();
	const [updateLiPost, { error: updateError, isLoading: updateIsLoading }] =
		useUpdateLiPostMutation();

	const suggestions = useLinkedinSuggestions();
	const validation = usePostUrlValidation();
	const { resetPreview } = validation;

	const [additionalColumns, setAdditionalColumns] = useState<boolean>(false);

	// Bumped after each form hydration to remount the topic builder, which parses its
	// chips only on mount. The form is populated by `reset` *after* the first render,
	// so without this the builder would stay empty when editing an existing record.
	const [builderKey, setBuilderKey] = useState<number>(0);

	const {
		handleSubmit,
		setValue,
		reset,
		watch,
		formState: { errors, isValid },
		clearErrors,
	} = useForm<CreateLiPost>({
		defaultValues: initialState,
		resolver: zodResolver(CreateLiPostSchema),
		reValidateMode: "onBlur",
	});

	const formValues = watch();

	useEffect(() => {
		if (isValid) {
			clearErrors();
		}
	}, [isValid, clearErrors]);

	useEffect(() => {
		if (liPost) {
			if (hasAdditionalColumns(liPost)) {
				setAdditionalColumns(true);
			}

			const newInitialState = mapLiPostToFormState(liPost);

			if (handleDuplicate) {
				reset({ ...newInitialState });
			} else {
				reset({ id: liPost.id, ...newInitialState });
			}
		} else {
			reset({
				...initialState,
				type: resolveSourceMode(Object.values(LiPostType), initialState.type),
			});
		}

		// Remount the topic builder so it re-parses chips from the freshly reset query.
		setBuilderKey((key) => key + 1);
	}, [list.scrapeLevel, liPost, reset, handleDuplicate]);

	// Clear the post preview whenever the URL or source type changes.
	useEffect(() => {
		resetPreview();
	}, [formValues.postUrl, formValues.type, resetPreview]);

	const onSubmit = async (values: CreateLiPost) => {
		try {
			const payload = normalizeLiPostPayload(values);

			if (liPost?.id && !handleDuplicate) {
				await updateLiPost({ ...payload, id: liPost.id, listId: list.id }).unwrap();
			} else {
				await createLiPost({ ...payload, listId: list.id }).unwrap();
			}

			reset(initialState);
			resetPreview();
			handleSelect?.(null);
			handleDuplicate?.(null);
		} catch (err) {
			toastFailure(err, t("errors.saveFailed"));
		}
	};

	const showsTopicTooLong =
		formValues.type === LiPostType.BY_PROFILE &&
		errors.query?.message === LI_POST_QUERY_TOO_LONG_MESSAGE;

	const formErrors = Object.values(errors)
		.map((error) => error?.message)
		.filter((message) => !!message && message !== LI_POST_QUERY_TOO_LONG_MESSAGE) as ERROR_TYPE[];

	const buttonTitle =
		handleDuplicate && liPost?.id
			? t("basics.copyCriteria")
			: liPost?.id
				? t("basics.save")
				: t("basics.addToList");

	return (
		<div className="px-4 pt-4 mt-8 rounded-xl border border-gray-200 bg-brand-white">
			<div className="text-smd mb-5">Add a source</div>

			<LiPostTypeSelector
				selectedType={formValues.type}
				onSelect={(type) => setValue("type", type, { shouldDirty: true, shouldValidate: true })}
			/>

			{formValues.type === LiPostType.BY_PROFILE && (
				<ProfileSourceSection
					errors={errors}
					formValues={formValues}
					setValue={setValue}
					suggestions={suggestions}
				/>
			)}

			{formValues.type === LiPostType.BY_TOPIC && (
				<TopicQueryBuilder
					key={`topic-builder-${liPost?.id ?? "new"}-${handleDuplicate ? "dup" : "edit"}-${builderKey}`}
					error={!!errors.query?.message}
					initialQuery={formValues.query ?? ""}
					onJobChangeEnabled={() =>
						setValue("posts", true, { shouldDirty: true, shouldValidate: true })
					}
					onQueryChange={(query) =>
						setValue("query", query, { shouldDirty: true, shouldValidate: true })
					}
				/>
			)}

			{formValues.type === LiPostType.BY_URL && (
				<PostUrlSection
					errors={errors}
					formValues={formValues}
					setValue={setValue}
					validation={validation}
				/>
			)}

			{formValues.type !== LiPostType.BY_URL && (
				<TimeFrameField errors={errors} formValues={formValues} setValue={setValue} />
			)}

			{formValues.type === LiPostType.BY_PROFILE && (
				<ProfileQueryField errors={errors} formValues={formValues} setValue={setValue} />
			)}

			{formValues.type === LiPostType.BY_TOPIC && (
				<TopicPostFiltersSection
					additionalColumns={additionalColumns}
					errors={errors}
					formValues={formValues}
					setValue={setValue}
					suggestions={suggestions}
					onToggleAdditionalColumns={() => setAdditionalColumns(!additionalColumns)}
				/>
			)}

			<LeadListSourceSection errors={errors} formValues={formValues} setValue={setValue} />

			{showsTopicTooLong && (
				<div className="mb-4 text-xs text-status-error">{LI_POST_QUERY_TOO_LONG_MESSAGE}</div>
			)}

			<LiPostFormActions
				buttonTitle={buttonTitle}
				formErrors={formErrors}
				isLoading={isLoading || updateIsLoading}
				serverError={error || updateError}
				onCancel={
					handleSelect
						? () => {
								reset(initialState);
								resetPreview();
								handleSelect?.(null);
								handleDuplicate?.(null);
							}
						: undefined
				}
				onSubmit={handleSubmit(onSubmit)}
			/>
		</div>
	);
};
